import { FolderIcon, ChevronRight, Check, ChevronsUpDown, Inbox, Search, FolderPlus, Loader2 } from "lucide-react";
import { useState, useRef, useEffect, useMemo } from "react";
import { toast } from "sonner";

import { useCreateFolderMutation, useSidebarData } from "@/modules/mine";
import type { FolderData } from "@/modules/mine/types";
import { cn } from "@/shared/lib/utils";
import { Button } from "@/shared/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/popover";

// 文件夹选项接口（UI 展示用）
interface FolderOption {
  id: string;
  name: string;
  children?: FolderOption[];
}

interface FolderSearchOption {
  id: string;
  name: string;
  parentNames: string[];
}

function flattenFolders(
  folders: FolderOption[],
  parentNames: string[] = [],
): FolderSearchOption[] {
  return folders.flatMap<FolderSearchOption>((folder) => {
    const currentNames = [...parentNames, folder.name];
    const children = folder.children?.length
      ? flattenFolders(folder.children, currentNames)
      : [];

    return [
      {
        id: folder.id,
        name: folder.name,
        parentNames,
      },
      ...children,
    ];
  });
}

// 树形文件夹选择项组件
interface FolderTreeItemProps {
  folder: FolderOption;
  selectedId: string | null;
  onSelect: (id: string) => void;
  expandedIds: Set<string>;
  onToggleExpand: (id: string) => void;
  level?: number;
  shouldShowPlaceholder?: boolean;
}

function FolderTreeItem({ 
  folder, 
  selectedId, 
  onSelect, 
  expandedIds, 
  onToggleExpand, 
  level = 0,
  shouldShowPlaceholder = false,
}: FolderTreeItemProps) {
  const hasChildren = folder.children && folder.children.length > 0;
  const isSelected = selectedId === folder.id;
  const isExpanded = expandedIds.has(folder.id);

  if (!hasChildren) {
    const showPlaceholder = level !== 0 || shouldShowPlaceholder;
    return (
      <div 
        data-folder-id={folder.id}
        className={cn(
          "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent",
          isSelected && "bg-accent font-medium"
        )}
      >
        {/* 占位元素：子文件夹始终显示，顶级文件夹根据情况显示 */}
        {showPlaceholder && (
          <span className="flex items-center justify-start">
            <ChevronRight className="size-4 opacity-0 pointer-events-none" />
          </span>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect(folder.id);
          }}
          className="flex w-full items-center gap-2"
        >
          <FolderIcon className="size-4 shrink-0" />
          <span className="flex-1 text-left">{folder.name}</span>
          {isSelected && <Check className="size-4 shrink-0" />}
        </button>
      </div>
    );
  }

  return (
    <Collapsible 
      open={isExpanded}
      onOpenChange={() => onToggleExpand(folder.id)}
      className="[&[data-state=open]>div>button:first-child>svg]:rotate-90"
    >
      <div 
        data-folder-id={folder.id}
        className={cn(
          "flex items-center gap-2 py-1.5 px-2 hover:bg-accent rounded-md",
          isSelected && "bg-accent font-medium"
        )}
      >
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex items-center justify-start"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <ChevronRight className="size-4 transition-transform" />
          </button>
        </CollapsibleTrigger>
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onSelect(folder.id);
          }}
          className="flex flex-1 items-center gap-2 text-sm"
        >
          <FolderIcon className="size-4 shrink-0" />
          <span className="flex-1 text-left">{folder.name}</span>
          {isSelected && <Check className="size-4 shrink-0" />}
        </button>
      </div>
      <CollapsibleContent className="ml-[16px] pl-2 border-l border-border/50 flex flex-col gap-0.5">
        <div className="mt-1 space-y-1">
          {folder.children?.map((child) => (
            <FolderTreeItem
              key={child.id}
              folder={child}
              selectedId={selectedId}
              onSelect={onSelect}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              level={level + 1}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

// 组件 Props
export interface FolderSelectorProps {
  value: string | null;                    // 当前选中的文件夹 ID
  onChange: (folderId: string | null) => void;  // 选中回调
  placeholder?: string;                     // 占位符文本
  allowEmpty?: boolean;                     // 是否允许不选择
  emptyLabel?: string;                      // "不选择"选项的文本
  emptyIcon?: React.ElementType;           // "不选择"选项的图标
  className?: string;                       // 自定义样式
  returnDropdownContent?: boolean;          // 是否返回下拉内容
  showSearch?: boolean;                     // 是否显示搜索框
  allowCreate?: boolean;                    // 搜索无结果时是否允许创建文件夹
  createLabel?: string;                     // 创建选项的描述文本
  filterFolderId?: string;                   // 过滤的文件夹 ID
  emptySelected?: boolean;                   // 是否将空选项显示为已选
  portalledDropdown?: boolean;               // 是否将下拉层渲染到滚动容器之外
}

/**
 * 文件夹选择器组件
 */
export function FolderSelector({
  value,
  onChange,
  placeholder = "选择文件夹",
  allowEmpty = false,
  emptyLabel = "不选择",
  emptyIcon: EmptyIcon,
  className,
  returnDropdownContent = false,
  showSearch = true,
  allowCreate = true,
  createLabel = "创建",
  filterFolderId,
  emptySelected,
  portalledDropdown = false,
}: FolderSelectorProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const createFolderMutation = useCreateFolderMutation();

  // 从 Zustand Store 获取文件夹数据（首次使用时自动加载）
  const { folders: foldersData } = useSidebarData();

  // 转换文件夹数据格式（递归），同时过滤掉指定的文件夹及其所有子集
  const convertFolderData = (folderData: FolderData): FolderOption | null => {
    // 如果当前文件夹是需要过滤的，返回 null
    if (filterFolderId && String(folderData.id) === filterFolderId) {
      return null;
    }
    
    const folder: FolderOption = {
      id: String(folderData.id),
      name: folderData.name,
    };
    
    if (folderData.sub_folders && folderData.sub_folders.length > 0) {
      // 递归转换子文件夹，并过滤掉返回 null 的项
      const children = folderData.sub_folders
        .map(convertFolderData)
        .filter((child): child is FolderOption => child !== null);
      
      if (children.length > 0) {
        folder.children = children;
      }
    }
    
    return folder;
  };

  const folders: FolderOption[] = foldersData
    .map(convertFolderData)
    .filter((folder): folder is FolderOption => folder !== null);

  const normalizedSearchQuery = searchQuery.trim().toLowerCase();
  const searchResults = useMemo(() => {
    if (!normalizedSearchQuery) {
      return [];
    }

    return flattenFolders(folders).filter((folder) =>
      folder.name.toLowerCase().includes(normalizedSearchQuery),
    );
  }, [folders, normalizedSearchQuery]);
  const isSearching = normalizedSearchQuery.length > 0;

  // 点击外部关闭下拉框
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
        setSearchQuery("");
      }
    };
    if (dropdownOpen && !portalledDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen, portalledDropdown]);

  // 切换文件夹展开/收起状态
  const handleToggleExpand = (folderId: string) => {
    setExpandedFolderIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(folderId)) {
        newSet.delete(folderId);
      } else {
        newSet.add(folderId);
      }
      return newSet;
    });
  };

  // 获取选中的文件夹名称（递归查找）
  const getSelectedFolderName = (folderId: string | null, folders: FolderOption[]): string => {
    if (!folderId) return placeholder;
    for (const folder of folders) {
      if (folder.id === folderId) return folder.name;
      if (folder.children) {
        const name = getSelectedFolderName(folderId, folder.children);
        if (name !== placeholder) return name;
      }
    }
    return placeholder;
  };

  const hasAnyTopLevelFolderWithChildren = folders.some(
    (folder) => folder.children && folder.children.length > 0
  );
  const isEmptySelected = emptySelected ?? !value;

  const selectFolder = (folderId: string | null) => {
    onChange(folderId);
    setDropdownOpen(false);
    setSearchQuery("");
  };

  const selectSearchResult = (folderId: string) => {
    onChange(folderId);
  };

  const handleCreateFolder = () => {
    const folderName = searchQuery.trim();

    if (!folderName || createFolderMutation.isPending) {
      return;
    }

    createFolderMutation.mutate(
      { name: folderName },
      {
        onSuccess: (folder) => {
          toast.success("文件夹创建成功");
          selectFolder(String(folder.id));
        },
      },
    );
  };

  // 下拉内容 JSX（直接渲染，避免作为内部组件导致重新挂载）
  const dropdownContent = (
    <div className={cn(
      "w-full",
      !returnDropdownContent && !portalledDropdown && "absolute z-50 mt-1 overflow-hidden rounded-md border bg-popover shadow-md"
    )}>
      {/* 搜索框 */}
      {showSearch && <div className={cn("relative mb-2", !returnDropdownContent && "mx-2 mt-2")}>
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
          <Input
            type="text"
            placeholder={allowCreate ? "搜索或创建文件夹..." : "搜索文件夹..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                allowCreate &&
                searchResults.length === 0 &&
                searchQuery.trim()
              ) {
                e.preventDefault();
                handleCreateFolder();
              }
            }}
            className="pl-9 h-9 text-xs" 
          />
        </div>}
      
      {/* 文件夹列表 */}
      <div
        className={cn(
          "max-h-[300px] overflow-y-auto p-2",
          returnDropdownContent && "rounded-md border bg-popover"
        )}
      >
            {/* "不选择"选项 */}
            {allowEmpty && !isSearching && (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  selectFolder(null);
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded-md py-1.5 px-2 text-sm hover:bg-accent mb-1",
                  isEmptySelected && "bg-accent font-medium"
                )}
              >
                {hasAnyTopLevelFolderWithChildren && (
                  <ChevronRight className="size-4 opacity-0 pointer-events-none" />
                )}
                {EmptyIcon ? (
                  <EmptyIcon className="size-4 shrink-0" />
                ) : (
                  emptyLabel === "未分类" && <Inbox className="size-4 shrink-0" />
                )}
                <span className="flex-1 text-left">{emptyLabel}</span>
                {isEmptySelected && <Check className="size-4 shrink-0" />}
              </button>
            )}

            {isSearching ? (
              searchResults.length > 0 ? (
                <div className="space-y-1">
                  {searchResults.map((folder) => (
                    <button
                      key={folder.id}
                      type="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        selectSearchResult(folder.id);
                      }}
                      className={cn(
                        "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent",
                        value === folder.id && "bg-accent font-medium"
                      )}
                    >
                      <FolderIcon className="size-4 shrink-0" />
                      <span className="flex min-w-0 flex-1 items-center gap-1 truncate text-left">
                        {folder.parentNames.map((parentName, index) => (
                          <span
                            key={`${folder.id}-${index}-${parentName}`}
                            className="min-w-0 truncate text-muted-foreground"
                          >
                            {parentName}
                            <span className="ml-1 text-muted-foreground/60">/</span>
                          </span>
                        ))}
                        <span className="min-w-0 truncate font-medium">
                          {folder.name}
                        </span>
                      </span>
                      {value === folder.id && <Check className="size-4 shrink-0" />}
                    </button>
                  ))}
                </div>
              ) : (
                allowCreate ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleCreateFolder();
                    }}
                    disabled={createFolderMutation.isPending}
                    className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {createFolderMutation.isPending ? (
                      <Loader2 className="size-4 shrink-0 animate-spin" />
                    ) : (
                      <FolderPlus className="size-4 shrink-0" />
                    )}
                    <span className="min-w-0 flex-1 truncate text-left">
                      {createLabel} <span className="font-medium text-primary">{searchQuery.trim()}</span>
                    </span>
                  </button>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 py-8 text-muted-foreground">
                    <span className="flex size-8 items-center justify-center rounded-full bg-muted/60">
                      <Search className="size-4 opacity-60" />
                    </span>
                    <span className="text-sm text-muted-foreground/70">
                      没有找到匹配的文件夹
                    </span>
                  </div>
                )
              )
            ) : folders.length > 0 ? (
              <div className="space-y-1">
                {folders.map((folder) => (
                  <FolderTreeItem
                    key={folder.id}
                    folder={folder}
                    selectedId={value}
                    onSelect={selectFolder}
                    expandedIds={expandedFolderIds}
                    onToggleExpand={handleToggleExpand}
                    shouldShowPlaceholder={hasAnyTopLevelFolderWithChildren}
                  />
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground text-center py-2">
                暂无文件夹
              </div>
            )}
      </div>
    </div>
  );

  if (returnDropdownContent) {
    return dropdownContent;
  }

  const trigger = (
    <Button
      aria-expanded={dropdownOpen}
      className="w-full justify-between text-sm font-normal"
      onClick={portalledDropdown ? undefined : () => setDropdownOpen(!dropdownOpen)}
      role="combobox"
      type="button"
      variant="outline"
    >
      <span className="flex items-center gap-2">
        <FolderIcon className="size-4 opacity-50" />
        {getSelectedFolderName(value, folders)}
      </span>
      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
    </Button>
  );

  if (portalledDropdown) {
    return (
      <div className={className}>
        <Popover
          modal
          onOpenChange={(open) => {
            setDropdownOpen(open);
            if (!open) setSearchQuery("");
          }}
          open={dropdownOpen}
        >
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
          <PopoverContent
            align="start"
            className="w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
            collisionPadding={8}
          >
            {dropdownContent}
          </PopoverContent>
        </Popover>
      </div>
    );
  }

  return (
    <div className={cn("relative", className)} ref={dropdownRef}>
      {trigger}
      {dropdownOpen && dropdownContent}
    </div>
  );
}
